import { isReviewedVideo } from './videoWorkflow';
import { getOrganizationCommercialState } from './billing';
import { normalizeEntitlementRecord } from './contentPackAccess';

export function getInstructorWorkloadSummary({
  instructorId = '',
  lessons = [],
  videos = [],
} = {}) {
  const assignedLessons = (lessons || []).filter(
    (lesson) =>
      lesson.assignedInstructorId === instructorId || lesson.instructorId === instructorId
  );
  const assignedVideos = (videos || []).filter(
    (video) =>
      video.assignedInstructorId === instructorId || video.assignedTo === instructorId
  );

  return {
    assignedLessonCount: assignedLessons.length,
    assignedVideoCount: assignedVideos.length,
    pendingLessonCount: assignedLessons.filter((lesson) => lesson.status !== 'completed').length,
    pendingVideoCount: assignedVideos.filter((video) => !isReviewedVideo(video)).length,
    completedReviewCount: assignedVideos.filter(isReviewedVideo).length,
    totalAssignedCount: assignedLessons.length + assignedVideos.length,
  };
}

export function getOrganizationInstructorWorkload({
  instructors = [],
  lessons = [],
  videos = [],
} = {}) {
  return (instructors || []).map((instructor) => ({
    instructorId: instructor.id,
    instructorName: instructor.name || instructor.email || instructor.id,
    ...getInstructorWorkloadSummary({
      instructorId: instructor.id,
      lessons,
      videos,
    }),
  }));
}

export function getOrganizationAssignmentSummary({
  lessons = [],
  videos = [],
} = {}) {
  const assignedLessonCount = (lessons || []).filter(
    (lesson) => lesson.assignedInstructorId || lesson.instructorId
  ).length;
  const assignedVideoCount = (videos || []).filter(
    (video) => video.assignedInstructorId || video.assignedTo
  ).length;

  return {
    lessonCount: (lessons || []).length,
    videoCount: (videos || []).length,
    assignedLessonCount,
    unassignedLessonCount: Math.max((lessons || []).length - assignedLessonCount, 0),
    assignedVideoCount,
    unassignedVideoCount: Math.max((videos || []).length - assignedVideoCount, 0),
    completedReviewCount: (videos || []).filter(isReviewedVideo).length,
  };
}

export function getOrganizationInviteSummary({
  invites = [],
  members = [],
} = {}) {
  const normalizedInvites = invites || [];
  const pendingInvites = normalizedInvites.filter((invite) => invite.status === 'pending');

  return {
    inviteCount: normalizedInvites.length,
    pendingInviteCount: pendingInvites.length,
    acceptedInviteCount: normalizedInvites.filter((invite) => invite.status === 'accepted').length,
    declinedInviteCount: normalizedInvites.filter((invite) => invite.status === 'declined').length,
    pendingMemberCount: (members || []).filter((member) => (member.status || '') === 'pending_invite').length,
  };
}

export function getOrganizationPremiumUsageSummary({
  templates = [],
  contentPacks = [],
  entitlements = [],
  commercialState = null,
} = {}) {
  const premiumTemplates = (templates || []).filter(
    (template) => template.accessTier === 'premium' || Boolean(template.contentPackId)
  );

  return {
    premiumTemplateCount: premiumTemplates.length,
    organizationContentCount: (templates || []).filter((template) => template.ownerType === 'organization').length,
    instructorPrivateContentCount: (templates || []).filter((template) => template.ownerType === 'instructor').length,
    platformContentCount: (templates || []).filter((template) => template.ownerType === 'platform').length,
    accessiblePremiumPackCount: commercialState?.includedPacks?.length || 0,
    addOnPremiumPackCount: commercialState?.addOnPacks?.length || 0,
    entitlementCount: (entitlements || []).filter((entitlement) => entitlement.status !== 'revoked').length,
    contentPackCount: (contentPacks || []).length,
  };
}

export function getOrganizationCommercialAnalytics({
  subscription = null,
  subscriptionPlans = [],
  entitlements = [],
  contentPacks = [],
  organizationId = '',
  instructorCount = 0,
} = {}) {
  const commercialState = getOrganizationCommercialState(organizationId, {
    subscription,
    subscriptionPlans,
    entitlements,
    contentPacks,
  }) || {};

  const seatLimit = commercialState.instructorSeatLimit || 0;
  const seatUtilization =
    seatLimit > 0 ? Math.min(100, Math.round((instructorCount / seatLimit) * 100)) : null;

  return {
    ...commercialState,
    activeSubscriptionCount: commercialState.subscription ? 1 : 0,
    trialStatus: commercialState.isTrialing ? 'trialing' : (commercialState.subscription?.status || 'inactive'),
    seatUtilizationPercent: seatUtilization,
  };
}

export function getOrganizationAnalytics({
  organizationId = '',
  members = [],
  instructors = [],
  students = [],
  lessons = [],
  videos = [],
  templates = [],
  invites = [],
  contentPacks = [],
  entitlements = [],
  subscription = null,
  subscriptionPlans = [],
} = {}) {
  const assignmentSummary = getOrganizationAssignmentSummary({ lessons, videos });
  const inviteSummary = getOrganizationInviteSummary({ invites, members });
  const instructorWorkload = getOrganizationInstructorWorkload({ instructors, lessons, videos });
  const commercialAnalytics = getOrganizationCommercialAnalytics({
    subscription,
    subscriptionPlans,
    entitlements,
    contentPacks,
    organizationId,
    instructorCount: instructors.length,
  });
  const premiumUsageSummary = getOrganizationPremiumUsageSummary({
    templates,
    contentPacks,
    entitlements,
    commercialState: commercialAnalytics,
  });

  return {
    activeInstructorCount: instructors.length,
    activeStudentCount: students.length,
    assignmentSummary,
    inviteSummary,
    instructorWorkload,
    premiumUsageSummary,
    commercialAnalytics,
  };
}

export function getPlatformPlanDistribution({
  subscriptions = [],
  plans = [],
} = {}) {
  const planNamesByCode = new Map(
    (plans || []).map((plan) => [plan.code || plan.subscriptionPlanId, plan.name || plan.code || 'Unknown Plan'])
  );

  return (subscriptions || []).reduce((acc, subscription) => {
    const key = subscription.planCode || subscription.subscriptionPlanId || 'unassigned';
    const label = planNamesByCode.get(key) || key;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
}

export function getPlatformEntitlementSourceBreakdown(entitlements = []) {
  return (entitlements || [])
    .map((entitlement) => normalizeEntitlementRecord(entitlement))
    .filter((entitlement) => entitlement.status !== 'revoked')
    .reduce((acc, entitlement) => {
      const key = entitlement.sourceType || 'manual_grant';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
}

export function getPlatformPremiumPackUsage({
  contentPacks = [],
  entitlements = [],
  templates = [],
  subscriptions = [],
} = {}) {
  const premiumPacks = (contentPacks || []).filter((contentPack) => contentPack.isPremium);
  const planCodesInUse = new Set((subscriptions || []).map((subscription) => subscription.planCode).filter(Boolean));

  return {
    premiumPackCount: premiumPacks.length,
    organizationsWithPremiumAccess: new Set(
      (entitlements || [])
        .map((entitlement) => normalizeEntitlementRecord(entitlement))
        .filter((entitlement) => entitlement.status !== 'revoked')
        .map((entitlement) => entitlement.organizationId)
        .filter(Boolean)
    ).size,
    premiumTemplateCount: (templates || []).filter(
      (template) => template.accessTier === 'premium' || Boolean(template.contentPackId)
    ).length,
    activePlanCodesWithPremiumAccess: planCodesInUse.size,
  };
}

export function getPlatformAnalytics({
  organizations = [],
  organizationMembers = [],
  invites = [],
  lessons = [],
  videos = [],
  templates = [],
  contentPacks = [],
  entitlements = [],
  subscriptions = [],
  plans = [],
} = {}) {
  const planDistribution = getPlatformPlanDistribution({ subscriptions, plans });
  const entitlementSourceBreakdown = getPlatformEntitlementSourceBreakdown(entitlements);
  const premiumUsage = getPlatformPremiumPackUsage({
    contentPacks,
    entitlements,
    templates,
    subscriptions,
  });

  const activeOrganizations = (organizations || []).filter((organization) => (organization.status || 'active') === 'active').length;
  const instructorCount = (organizationMembers || []).filter((member) =>
    ['organization_owner', 'organization_admin', 'instructor'].includes(member.role || member.orgRole)
  ).length;
  const studentCount = (organizationMembers || []).filter((member) => (member.role || member.orgRole) === 'student').length;

  return {
    organizationCount: (organizations || []).length,
    activeOrganizationCount: activeOrganizations,
    instructorCount,
    studentCount,
    pendingInviteCount: (invites || []).filter((invite) => invite.status === 'pending').length,
    lessonCount: (lessons || []).length,
    videoCount: (videos || []).length,
    completedReviewCount: (videos || []).filter(isReviewedVideo).length,
    activeSubscriptionCount: (subscriptions || []).filter((subscription) => subscription.status === 'active').length,
    trialOrganizationCount: (subscriptions || []).filter((subscription) => subscription.status === 'trialing').length,
    planDistribution,
    entitlementSourceBreakdown,
    premiumUsage,
  };
}
